import {
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { CloseIcon, MagnifyIcon } from '@atgfr8/icons';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import { debounce, get } from 'lodash';
import {
  createEffect,
  createSignal,
  For,
  JSX,
  mergeProps,
  onCleanup,
  onMount,
  Setter,
} from 'solid-js';

import ClickAwayListener from './ClickAwayListener';
import { AutocompleteProps } from './types';

const defaultValue = {
  debounceDelay: 300,
  debounce: true,
  defaultValue: '',
  error: null,
  forceAutoSelect: false,
  mode: 'single',
  isStatic: false,
  minCharacters: 2,
};

// eslint-disable-next-line complexity
export const Autocomplete = <T,>(props: AutocompleteProps<T>) => {
  const mp = mergeProps(defaultValue, props);
  const getResolvedInputValue = () =>
    props.value != null ? props.value : (mp.defaultValue as string);

  const [anchorEl, setAnchorEl] = createSignal<HTMLInputElement | undefined>();
  const [loading, setLoading] = createSignal<boolean>(false);
  const [isDropdownOpen, setDropdownOpen] = createSignal<boolean>(false);
  const [searchQuery, setSearchQuery] = createSignal<string>(
    getResolvedInputValue(),
  );
  const [searchResults, setSearchResults] = createSignal<T[]>([]);
  const [searchError, setSearchError] = createSignal<string | null>(null);
  const [activeIndex, setActiveIndex] = createSignal(-1);
  let listItemRef: HTMLLIElement[] = [];
  let lastSyncedValue = getResolvedInputValue();
  // Bumped whenever a search is superseded (new query/clear), so a slower
  // in-flight request's success/error callbacks can detect they're stale
  // and avoid clobbering a newer request's results with old data.
  let requestGeneration = 0;

  createEffect(() => {
    const nextValue = getResolvedInputValue();

    if (nextValue === lastSyncedValue) {
      return;
    }

    lastSyncedValue = nextValue;
    setSearchQuery(nextValue);
  });

  const handleInputFocus = (event: MouseEvent) => {
    if (searchQuery() && searchResults().length > 0) {
      setDropdownOpen(true);
    }

    event.stopPropagation();
  };

  const debounceDelay = 1000; // milliseconds

  const performSearch = debounce(async (query: string) => {
    const charactersLimit = mp.minCharacters;
    const trimmedQuery = query.trim();

    setSearchError(null);

    if (
      (trimmedQuery.length > charactersLimit && query !== '') ||
      Boolean(props.isStatic)
    ) {
      const currentGeneration = ++requestGeneration;
      const isStale = () => currentGeneration !== requestGeneration;

      setLoading(true);
      props.fetchData &&
        (await props.fetchData(
          query,
          (items) => {
            if (!isStale()) setSearchResults(items);
          },
          (message) => {
            if (!isStale()) setSearchError(message);
          },
        ));

      if (isStale()) return;

      if (Boolean(props.forceAutoSelect) && searchResults().length === 1) {
        handleSelect(searchResults()[0]);
        setDropdownOpen(false);
      } else {
        setDropdownOpen(true);
      }

      setLoading(false);
    } else {
      requestGeneration++;
      setSearchResults([]);
      setLoading(false);
      // Keep the dropdown open to surface the below-min-characters hint.
      setDropdownOpen(
        Boolean(props.belowMinCharactersMessage) && trimmedQuery.length > 0,
      );
    }
  }, debounceDelay);

  const handleRetry = () => {
    setSearchError(null);
    void performSearch(searchQuery());
  };

  const handleInputChange = (e: Event) => {
    setDropdownOpen(false);
    setSearchError(null);

    // NOTE: set activeIndex to 0 and reset listItemref when search query changes
    listItemRef = [];
    setSearchResults([]);
    setActiveIndex(0);
    const target = e.target as HTMLInputElement;

    if (props.phoneInput ?? false) {
      const cursorPosition = target.selectionStart;
      const oldValue = target.value;
      const oldLength = oldValue.length;
      const newUnformattedValue = oldValue.replace(/[^0-9]/g, '');

      if (newUnformattedValue.length <= 20) {
        const newValue = formatPhoneNumber(newUnformattedValue);
        const newLength = newValue.length;
        setSearchQuery(newValue);
        mp.onChange && mp.onChange(newUnformattedValue);

        if (cursorPosition !== null) {
          const diff = newLength - oldLength;

          target.setSelectionRange(
            cursorPosition + diff,
            cursorPosition + diff,
          );
        }
      }

      void performSearch(newUnformattedValue);
    } else {
      setSearchQuery(target.value);
      mp.onChange && mp.onChange(target.value);
      void performSearch(target.value);
    }
  };

  const handleSelect = (item: T, isProgrammatic = false) => {
    (props.shouldPerformSearch ?? false) && setSearchQuery('');
    setDropdownOpen(false);
    if (props.onItemSelect) {
      props.onItemSelect(item, isProgrammatic);
    }
    setActiveIndex(-1);
    Boolean(props.clearSearch) && handleClearSearch();
  };

  const handleEnterSelect = () => {
    if (
      isNaN(activeIndex()) ||
      activeIndex() < 0 ||
      activeIndex() >= searchResults().length
    ) {
      return;
    }

    handleSelect(searchResults()[activeIndex()], true);
  };

  const handleClearSearch = () => {
    performSearch.cancel();
    requestGeneration++;
    setSearchQuery('');
    setDropdownOpen(false);
    setLoading(false);
    if (props.onClearValue) {
      props.onClearValue();
      setSearchResults([]);
    }
    setActiveIndex(-1);
  };

  createEffect(() => {
    if (props.clearSearch ?? false) {
      handleClearSearch();
      setSearchResults([]);
    }
  });

  function renderEndAdornment() {
    if (loading()) {
      return <CircularProgress class="p-2!" />;
    } else if (searchQuery() && !Boolean(props.disabled)) {
      return (
        <CloseIcon
          style={{ cursor: 'pointer', 'z-index': '49' }}
          onClick={handleClearSearch}
        />
      );
    } else if (Boolean(props.hideIcon) || Boolean(props.disabled)) {
      return <></>;
    }

    return <MagnifyIcon style={{ cursor: 'pointer' }} />;
  }

  let observer: IntersectionObserver;

  const handleTextFieldInput = (e: Event) => {
    handleInputChange(e);
    props.onInput?.(e);
  };

  onMount(() => {
    const textFieldElement = document.getElementById(
      props.id,
    ) as HTMLInputElement;
    if (Boolean(textFieldElement)) {
      observer = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            if (Boolean(!entry.isIntersecting)) {
              setDropdownOpen(false);
            }
          });
        },
        {
          root: null,
          threshold: 0.1,
        },
      );

      observer.observe(textFieldElement);
    }
    if (Boolean(props.isStatic)) {
      setDropdownOpen(true);
      props.staticItemList && setSearchResults(props.staticItemList);
    }
  });

  onCleanup(() => {
    if (Boolean(observer)) {
      observer.disconnect();
    }
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    //NOTE: Removed create effect causing issues with multiple triggers and used event callback from TextField
    if (e.key === 'Tab') {
      setDropdownOpen(false);
      setActiveIndex(-1);
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      handleEnterSelect();
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (searchResults().length > 0 && isDropdownOpen()) {
        setActiveIndex((prevIndex) => {
          if (e.key === 'ArrowUp') {
            if (prevIndex === -1 || prevIndex === 0) {
              return searchResults().length - 1;
            }
            return prevIndex - 1;
          }
          return (prevIndex + 1) % searchResults().length;
        });

        if (activeIndex() >= 0 && activeIndex() < listItemRef.length) {
          const activeElement = listItemRef[activeIndex()];
          activeElement.scrollIntoView({
            block: 'nearest',
          });
        }
      }
    }
  };

  const getMenuWidth = () => {
    const input = anchorEl();
    const fieldShell = input?.closest('label.input') as HTMLElement | null;

    if (!fieldShell) {
      return input?.getBoundingClientRect().width;
    }

    // Use offsetWidth which includes border
    return fieldShell.offsetWidth;
  };

  const getMenuLeftOffset = () => {
    const input = anchorEl();
    const fieldShell = input?.closest('label.input') as HTMLElement | null;

    if (!input || !fieldShell) {
      return 0;
    }

    const offset =
      input.getBoundingClientRect().left -
      fieldShell.getBoundingClientRect().left;

    return Math.max(0, offset);
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        setDropdownOpen(false);
        setActiveIndex(-1);
      }}
      innerRefClass={mp.innerRefClass}
    >
      <TextField
        {...((props.inputProps ?? {}) as Record<string, unknown>)}
        ref={setAnchorEl as Setter<HTMLInputElement | undefined>}
        {...({
          'on:blur': props.onBlur,
          'on:focus': (e: unknown) => handleInputFocus(e as MouseEvent),
          'on:input': handleTextFieldInput,
        } as Record<string, unknown>)}
        name={props.name}
        id={props.id}
        fullWidth
        required={props.required}
        autocomplete="off"
        value={searchQuery()}
        placeholder={props.placeholder}
        class={props.class}
        label={props.label}
        startIcon={props.startAdornment}
        endIcon={
          props.disableClear !== true
            ? ((props.customButton?.(loading()) as JSX.Element) ??
              renderEndAdornment())
            : undefined
        }
        onKeyDown={handleKeyDown}
        size={
          props.size === 'medium'
            ? 'md'
            : props.size === 'small'
              ? 'sm'
              : props.size
        }
        dataTestId={props.dataTestId ?? ''}
        error={Boolean(props.disabled) ? undefined : Boolean(props.error)}
        disabled={props.disabled}
        helperText={
          Array.isArray(props.error) && props.error.length > 0
            ? props.error[0]
            : ''
        }
      />
      {props.multipleValueRender &&
        props.mode === 'multiple' &&
        props.multipleValueRender()}
      {props.multipleSelectValues &&
        props.mode === 'multiple' &&
        props.multipleSelectValues.map((value, i) => (
          <Chip
            id={`${props.id}-chip-${i}`}
            text={<>{get(value, `${props.renderMultipleKey}`)}</>}
            isDeleteIcon={true}
            deleteIconColor="white"
            backgroundColor="#026EA1"
            class="text-white! rounded! m-[5px] mr-0!"
            onDelete={() => {
              props.onMultipleDelete && props.onMultipleDelete(value);
              handleClearSearch();
            }}
          />
        ))}
      <Menu
        anchorEl={anchorEl}
        class={`max-h-[226px]!
          ${
            Boolean(props.listClass)
              ? `${props.listClass}`
              : `overflow-auto! max-h-60! ${
                  searchResults().length > 0 ? '' : 'p-0!'
                }`
          }`}
        style={{
          display: isDropdownOpen() ? 'block' : 'none',
          // Default: fixed width matching the input. Global search opts into
          // `dropdownFitContent` to use min-width so the menu can grow wider.
          ...(() => {
            const menuWidth = getMenuWidth();

            if (!menuWidth) {
              return {};
            }

            const widthKey = Boolean(props.dropdownFitContent)
              ? 'min-width'
              : 'width';

            return { [widthKey]: `${menuWidth}px` };
          })(),
          'margin-left': (() => {
            const leftOffset = getMenuLeftOffset();
            return leftOffset !== 0 ? `-${leftOffset}px` : undefined;
          })(),
        }}
      >
        {searchError() && (
          <MenuItem
            class="hover:bg-gray-100 cursor-pointer"
            onClick={handleRetry}
          >
            <div class="flex flex-col">
              <div>{searchError()}</div>
              <div class="text-xs opacity-60 underline">Retry</div>
            </div>
          </MenuItem>
        )}
        {!searchError() &&
          Boolean(props.belowMinCharactersMessage) &&
          searchQuery().trim().length > 0 &&
          searchQuery().trim().length <= mp.minCharacters &&
          !loading() && (
            <MenuItem class="pointer-events-none">
              <div class="text-sm">{props.belowMinCharactersMessage}</div>
            </MenuItem>
          )}
        {!searchError() &&
          searchResults().length === 0 &&
          searchQuery().trim().length >
            (props.exactMatchField
              ? mp.minCharacters
              : Math.max(3, mp.minCharacters)) &&
          !loading() && (
            <MenuItem class="hover:bg-gray-100">
              <div class="flex flex-col">
                <div>{props.noExactMatchMessage ?? 'No results found'}</div>
                {!props.noExactMatchMessage && (
                  <div class="text-xs opacity-60">
                    Please try with a different name
                  </div>
                )}
              </div>
            </MenuItem>
          )}
        <For each={searchResults()}>
          {(item: T, i) => (
            <MenuItem
              onClick={() => handleSelect(item)}
              class={`hover:bg-gray-100 cursor-pointer ${
                activeIndex() === i() ? 'bg-gray-100' : ''
              }`}
              style={props.renderItemStyle ? props.renderItemStyle : {}}
              ref={(el: HTMLLIElement) => {
                listItemRef[i()] = el;
              }}
            >
              <div>
                {props.renderItem
                  ? props.renderItem(item)
                  : get(item, props.responseFieldName ?? 'name')}
              </div>
            </MenuItem>
          )}
        </For>
      </Menu>
    </ClickAwayListener>
  );
};
